import React from 'react';
import { forOwn, find, has } from 'lodash';
import { useIntl, FormattedMessage } from 'react-intl';
import { Typography, Box, Button, Link } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNotifications } from '@toolpad/core/useNotifications';

import Dialog from '../../DesignSystem/Dialog';
import useCreateReportForPost from '../../../Hooks/useCreateReportForPost';
import ApiErrorsFormatter from '../../../Utils/ApiErrorsFormatter';
import useApiQuery from '../../../Hooks/useApiQuery';
import queriesConfig from '../../../Services/queriesConfig';
import ControlledChipGroup from '../../ReactHookFormComponents/ControlledChipGroup';
import ControlledTextField from '../../ReactHookFormComponents/ControlledTextField';
import ButtonWithLoader from '../../ButtonWithLoader';
import { dashboardPostPath, postPath, spacePath } from '../../../Utils/routes';
import { getEthicsLinks, getTermsLinks } from '../../../Utils/WebsiteUrls';
import { Groups } from '../../../Utils/Types';
import { notify } from '../../../Utils/NotificationUtils';

function ReportPostDialog({ post, commentId, open, onClose, space }: any) {
  const intl = useIntl();
  const navigate = useNavigate();
  const notifications = useNotifications();
  const willDelete = Groups.CREATORS.includes(space.current_user_role);

  const { pathname } = useLocation();
  const {
    handleSubmit,
    control,
    watch,
    setError,
    formState: { errors },
    clearErrors,
    setValue,
  } = useForm({
    reValidateMode: 'onChange',

    defaultValues: {
      reason: null,
      primary_reason_key: null,
      secondary_reason_key: null,
    },
  });
  const { data: reportReasons } = useApiQuery({
    query: queriesConfig.getReportReasons(),
  });
  const { createReportForPost, loading } = useCreateReportForPost(post.id, commentId);

  const dialogTitle = intl.formatMessage({
    id: willDelete ? 'POSTS.DELETE.ACTION' : 'POSTS.REPORT.ACTION',
  });

  const hasSecondaryReason = (primaryReason: any) => !!primaryReason.secondary_reasons;
  const getSecondaryReason = (primaryReason: any, key: any) =>
    find(primaryReason.secondary_reasons, { key });

  const primaryReasonKey = watch('primary_reason_key');
  const primaryReason = find(reportReasons, { key: primaryReasonKey }) || {};
  const secondaryReasonKey = watch('secondary_reason_key');
  const secondaryReason = getSecondaryReason(primaryReason, secondaryReasonKey);

  const reasonRequired = primaryReason?.comment_required || secondaryReason?.comment_required;

  const renderPlaceHolder = () => {
    const hasPrimaryReasonPlaceholder = !!primaryReason?.placeholder;
    const hasSecondaryReasonPlaceholder = !!secondaryReason?.placeholder;

    if (hasPrimaryReasonPlaceholder) {
      return primaryReason.placeholder;
    }

    if (hasSecondaryReasonPlaceholder) {
      return secondaryReason?.placeholder;
    }

    return intl.formatMessage({
      id: willDelete ? 'POSTS.DELETE.PLACEHOLDER' : 'POSTS.REPORT.PLACEHOLDER',
    });
  };

  const onSubmit = async (report: any) => {
    clearErrors();

    const response = await createReportForPost({ report });

    if (response?.data?.errors) {
      const formattedErrors = ApiErrorsFormatter(response.data.errors);
      forOwn(formattedErrors, (message, key) => setError(key, message));
      notify(notifications, intl.formatMessage({ id: 'POSTS.REPORT.EMPTY_ERROR' }), 'error');

      return;
    }

    onClose();

    notify(
      notifications,
      intl.formatMessage({ id: willDelete ? 'POSTS.DELETE.SUCCESS' : 'POSTS.REPORT.SUCCESS' }),
    );

    if (willDelete && !commentId) {
      if (pathname.match(dashboardPostPath(space.slug, post.slug, post.type))) {
        navigate(spacePath('dashboard'));
      } else if (pathname.match(postPath(space.slug, post.slug))) {
        navigate(spacePath());
      }
    }

    return response;
  };

  const formId = `${post.id}-${commentId}-delete-or-report-dialog`;

  return (
    <form onSubmit={handleSubmit(onSubmit)} id={formId}>
      <Dialog
        open={open}
        onClose={onClose}
        actions={
          <>
            <Button variant="outlined" onClick={onClose} color="primary">
              <FormattedMessage id="BUTTONS.CANCEL" />
            </Button>
            <ButtonWithLoader
              variant="contained"
              type="submit"
              color="primary"
              form={formId}
              isFetching={loading}
            >
              <FormattedMessage id={`POSTS.${willDelete ? 'DELETE' : 'REPORT'}.CONFIRM`} />
            </ButtonWithLoader>
          </>
        }
        title={dialogTitle}
        label={dialogTitle}
        variant="large"
        id={formId}
      >
        <Typography variant="body1" color={errors.primary_reason_key ? 'error' : 'initial'}>
          <FormattedMessage
            id={willDelete ? 'POSTS.DELETE.SELECT_REASON' : 'POSTS.REPORT.SELECT_REASON'}
          />
        </Typography>
        <Box mb={1} mt={1} ml={-1}>
          <ControlledChipGroup
            control={control}
            value={primaryReason?.key || ''}
            name="primary_reason_key"
            data={reportReasons}
            resetError={() => clearErrors()}
            resetValues={() => setValue('secondary_reason_key', null)}
            row
          />
        </Box>

        {primaryReason && hasSecondaryReason(primaryReason) && (
          <>
            <Typography variant="body1" color={errors.secondary_reason_key ? 'error' : 'initial'}>
              {primaryReason.subtitle}
            </Typography>
            <Box mb={1} mt={1} ml={-1}>
              <ControlledChipGroup
                control={control}
                resetError={() => clearErrors()}
                value={watch('secondary_reason_key') || ''}
                name="secondary_reason_key"
                data={primaryReason.secondary_reasons}
                row
              />
            </Box>
          </>
        )}
        <Box my={2}>
          <ControlledTextField
            id="comment-on-report-input"
            value={watch('reason') || ''}
            name="reason"
            rules={{
              required: reasonRequired && intl.formatMessage({ id: 'FORM.ERRORS.REQUIRED' }),
            }}
            required={reasonRequired}
            control={control}
            resetError={() => clearErrors()}
            label={intl.formatMessage({ id: 'POSTS.DELETE.REASON' })}
            placeholder={renderPlaceHolder()}
            helperText={errors?.reason?.message}
            error={has(errors, 'reason.message')}
            multiline
            rows={4}
          />
        </Box>

        <Box display="flex" flexWrap="wrap">
          <Typography variant="body1">
            <FormattedMessage id="POSTS.REPORT.FLUICITY_TERMS_PART_A" />
          </Typography>
          <Link
            href={getTermsLinks(intl.locale)}
            target="_blank"
            variant="body1"
            style={{ marginLeft: 4, marginRight: 4 }}
            underline="hover"
            title={intl.formatMessage({ id: 'BUTTONS.OPEN_IN_NEW_TAB' })}
          >
            <FormattedMessage id="POSTS.REPORT.TERMS_OF_USE" />
          </Link>
          <Typography variant="body1">
            <FormattedMessage id="POSTS.REPORT.FLUICITY_TERMS_PART_B" />
          </Typography>
          <Link
            href={getEthicsLinks(intl.locale)}
            target="_blank"
            variant="body1"
            style={{ marginLeft: 4, marginRight: 4 }}
            underline="hover"
            title={intl.formatMessage({ id: 'BUTTONS.OPEN_IN_NEW_TAB' })}
          >
            <FormattedMessage id="POSTS.REPORT.CODE_OF_ETHICS" />
          </Link>
          <Typography variant="body1">
            <FormattedMessage id="POSTS.REPORT.FLUICITY_TERMS_PART_C" />
          </Typography>
        </Box>
      </Dialog>
    </form>
  );
}

export default ReportPostDialog;
